import type { Alerta } from '@wms/shared';
import type { DashboardResposta } from './tipos';

/**
 * A rota /api/dashboard não devolve `alertas[]` — ela devolve os números
 * crus (resumo + consolidação). A lista "precisa de atenção" é montada aqui,
 * a partir desses números, sem inventar nada: todo alerta abaixo tem uma
 * contagem real por trás e um `href` que abre a origem do dado.
 */
export function derivarAlertas(dados: DashboardResposta, centro: number): Alerta[] {
  const alertas: Alerta[] = [];
  const resumo = dados.resumo;

  if (resumo && resumo.posicoesComSaldoNegativo > 0) {
    alertas.push({
      tipo: 'saldo_negativo',
      severidade: 'critico',
      titulo: `${resumo.posicoesComSaldoNegativo} posições com saldo negativo`,
      detalhe: 'Saída registrada sem entrada correspondente. Dá para compensar transferindo de outra posição.',
      quantidade: resumo.posicoesComSaldoNegativo,
      href: `/movimentos/negativos?centro=${centro}`,
    });
  }

  const espalhados = dados.consolidacao.filter((c) => c.posicoes > 1);
  if (espalhados.length > 0) {
    const pior = espalhados[0];
    if (pior) {
      alertas.push({
        tipo: 'consolidacao',
        severidade: espalhados.length > 20 ? 'atencao' : 'info',
        titulo: `${espalhados.length} SKUs espalhados por várias posições`,
        detalhe: `Maior caso: SKU ${pior.produto} em ${pior.posicoes} posições.`,
        quantidade: espalhados.length,
        href: `/analises?centro=${centro}&p=consolidacao`,
      });
    }
  }

  if (resumo && resumo.estoqueSemEndereco > 0) {
    alertas.push({
      tipo: 'sem_endereco',
      severidade: 'atencao',
      titulo: `${resumo.estoqueSemEndereco} itens sem endereçamento`,
      detalhe: 'Saldo em LOCALIZADOR = 0: existe no estoque, não existe no mapa.',
      quantidade: resumo.estoqueSemEndereco,
      href: `/analises?centro=${centro}&p=sem-endereco`,
    });
  } else if (resumo && resumo.estoqueSemEndereco < 0) {
    // Saldo NEGATIVO no balde sem endereço não é estoque: é baixa sem entrada
    // correspondente. Mostrar "-27 itens sem endereçamento" seria absurdo, e
    // esconder seria pior — é sintoma de lançamento errado.
    alertas.push({
      tipo: 'sem_endereco',
      severidade: 'critico',
      titulo: `Saldo negativo fora de endereço (${resumo.estoqueSemEndereco})`,
      detalhe:
        'Em LOCALIZADOR = 0 saiu mais do que entrou. Não é estoque disponível — é baixa sem entrada correspondente no razão.',
      quantidade: Math.abs(resumo.estoqueSemEndereco),
      href: `/analises?centro=${centro}&p=sem-endereco`,
    });
  }

  const ruasCheias = dados.ocupacaoPorZona.filter((z) => z.percentual >= 90);
  if (ruasCheias.length > 0) {
    alertas.push({
      tipo: 'capacidade',
      severidade: 'atencao',
      titulo: `${ruasCheias.length} ${ruasCheias.length === 1 ? 'zona' : 'zonas'} acima de 90%`,
      detalhe: ruasCheias.map((z) => `${z.zona} (${z.percentual}%)`).join(', '),
      quantidade: ruasCheias.length,
      href: `/analises?centro=${centro}&p=ruas-cheias`,
    });
  }

  const ordem: Record<Alerta['severidade'], number> = { critico: 0, atencao: 1, info: 2 };
  return alertas.sort((a, b) => ordem[a.severidade] - ordem[b.severidade]);
}

export const CLASSE_SEVERIDADE: Record<Alerta['severidade'], string> = {
  critico: 'bg-status-divergenciaFundo text-status-divergencia border-status-divergencia/40',
  atencao: 'bg-status-atencaoFundo text-status-atencao border-status-atencao/40',
  info: 'bg-status-ocupadaFundo text-status-ocupada border-status-ocupada/40',
};
